import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaSyncAlt, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import CustomModal from '../../util_components/custom_modal';
import ChecklistForm from './form';
import DateRangeFilter from './DateRangeFilter';
import MultiSelectDropdown from './MultiSelectDropdown';
import { get_api_url } from '../../../utils/urls';
import { get_api } from '../../../redux/api_funcs';
import { image_error_replace } from '../../../util_functions';
import * as actions from '../../../redux/action_creators';

// Status filter tabs. "Pending" includes drafts (everything not submitted).
const STATUS_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Drafts', value: 'Drafts' },
  { label: 'Submitted', value: 'Submitted' }
];

// Checkpoint quick-filter tabs. Value is the check_type ('All' = no filter).
const CHECK_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Day 8', value: 'DAY_8' },
  { label: 'Day 15', value: 'DAY_15' },
  { label: 'Day 22', value: 'DAY_22' },
  { label: 'Week 7', value: 'WEEK_7' },
  { label: 'Week 11', value: 'WEEK_11' }
];

// Each row is one member at one checkpoint (Day 8 / 15 / 22, Week 7 / 11).
class EarlyRetentionChecklist extends Component {
  state = {
    items: [],
    loading: false,
    form_open: false,
    selected_item: null,
    status_filter: 'Pending',
    check_filter: 'All',
    date_from: '',
    date_to: '',
    agent_filter: [],
    sort_key: 'checkin_date',
    sort_dir: 'asc'
  };

  componentDidMount() {
    this.load_data();
  }

  load_data = () => {
    this.setState({ loading: true });
    const url = get_api_url('/v2/early_retention_checklist/list');
    get_api(url, true)
      .then((res) => {
        this.setState({ items: res.data.data.items || [], loading: false });
      })
      .catch((e) => {
        let err = 'Failed to load checklists';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  open_form = (item) => this.setState({ form_open: true, selected_item: item });

  close_form = () => this.setState({ form_open: false, selected_item: null });

  on_submitted = () => {
    this.close_form();
    this.load_data();
  };

  on_saved_draft = () => {
    this.load_data();
  };

  set_status_filter = (status_filter) => this.setState({ status_filter });

  set_check_filter = (check_filter) => this.setState({ check_filter });

  matches_check = (item) =>
    this.state.check_filter === 'All' || item.check_type === this.state.check_filter;

  set_date_range = ({ from, to }) => this.setState({ date_from: from, date_to: to });

  clear_date_range = () => this.setState({ date_from: '', date_to: '' });

  set_agent_filter = (agent_filter) => this.setState({ agent_filter });

  // Distinct ops agents present in the loaded rows, for the agent filter.
  agent_options = () => {
    const seen = {};
    this.state.items.forEach((i) => {
      if (i.agent_uuid && !seen[i.agent_uuid]) {
        seen[i.agent_uuid] = i.agent_name || i.agent_uuid;
      }
    });
    return Object.keys(seen)
      .map((uuid) => ({ value: uuid, label: seen[uuid] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  matches_agent = (item) => {
    const { agent_filter } = this.state;
    if (agent_filter.length === 0) return true;
    return agent_filter.includes(item.agent_uuid);
  };

  matches_status = (item) => {
    switch (this.state.status_filter) {
      case 'Pending':
        return item.status === 'pending' || item.status === 'draft';
      case 'Drafts':
        return item.status === 'draft';
      case 'Submitted':
        return item.status === 'submitted';
      default:
        return true;
    }
  };

  matches_date = (item) => {
    const { date_from, date_to } = this.state;
    if (!date_from && !date_to) return true;
    const iso = item.checkin_date_iso;
    if (!iso) return false;
    if (date_from && iso < date_from) return false;
    if (date_to && iso > date_to) return false;
    return true;
  };

  // Click a sortable column header: toggle direction if same key, else asc.
  toggle_sort = (sort_key) =>
    this.setState((prev) => ({
      sort_key,
      sort_dir: prev.sort_key === sort_key && prev.sort_dir === 'asc' ? 'desc' : 'asc'
    }));

  // Comparable value per sortable column.
  sort_value = (item, key) => {
    switch (key) {
      case 'member':
        return (item.student_name || '').toLowerCase();
      case 'check':
        // Order checkpoints Day 8 < 15 < 22 < Week 7 < Week 11.
        return item.week_number > 0 ? 100 + item.week_number : item.day_number;
      case 'checkin_date':
      default:
        return item.checkin_date_iso || '';
    }
  };

  get_filtered = () => {
    const { sort_key, sort_dir } = this.state;
    const rows = this.state.items.filter(
      (i) =>
        this.matches_status(i) &&
        this.matches_check(i) &&
        this.matches_date(i) &&
        this.matches_agent(i)
    );
    const dir = sort_dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const av = this.sort_value(a, sort_key);
      const bv = this.sort_value(b, sort_key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  check_badge = (item) => {
    const text = item.week_number > 0 ? `Week ${item.week_number}` : `Day ${item.day_number}`;
    return (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
        {text}
      </span>
    );
  };

  status_badge = (status) => {
    const map = {
      draft: { cls: 'bg-amber-50 text-amber-700', label: 'Draft' },
      submitted: { cls: 'bg-green-50 text-green-700', label: 'Submitted' },
      pending: { cls: 'bg-gray-100 text-gray-600', label: 'Pending' }
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  // "Overall Status of Member" (Red / Yellow / Green) denormalised from the
  // checklist. Empty until an overall status has been picked on the form.
  member_status_badge = (status) => {
    const map = {
      Red: '#dc2626',
      Yellow: '#d97706',
      Green: '#16a34a'
    };
    const bg = map[status];
    if (!bg) return <span className="text-gray-400">—</span>;
    return (
      <span
        className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
        style={{ background: bg }}
      >
        {status}
      </span>
    );
  };

  // Dark segmented tabs (matches the platform's status-filter style).
  render_segmented = (options, selected, on_select) => (
    <div className="inline-flex rounded-full" style={{ background: '#1e293b', padding: 2 }}>
      {options.map((o) => {
        const is_sel = selected === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => on_select(o.value)}
            style={{
              padding: '2px 10px',
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              background: is_sel ? '#ffffff' : 'transparent',
              color: is_sel ? '#1e293b' : '#cbd5e1'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );

  // Clickable, sortable column header. Always shows a sort affordance: a muted
  // double-arrow when inactive, an amber up/down when it's the active sort.
  render_sort_th = (label, key) => {
    const active = this.state.sort_key === key;
    const asc = this.state.sort_dir === 'asc';
    let Icon = FaSort;
    let color = '#9ca3af';
    if (active) {
      Icon = asc ? FaSortUp : FaSortDown;
      color = '#f59e0b';
    }
    return (
      <th
        className="text-left px-5 py-2 font-medium cursor-pointer select-none hover:bg-gray-100 transition-colors"
        onClick={() => this.toggle_sort(key)}
        title="Sort"
      >
        <span className="inline-flex items-center gap-1.5">
          <span style={active ? { color: '#b45309' } : undefined}>{label}</span>
          <Icon size={12} color={color} style={{ verticalAlign: 'middle' }} />
        </span>
      </th>
    );
  };

  render_row = (item) => {
    const submitted = item.status === 'submitted';
    return (
      <tr key={item.uuid} className="border-t border-gray-100 hover:bg-gray-50/60">
        <td className="text-left px-5 py-3">
          <div className="flex items-center gap-3">
            <img
              src={item.profile_photo}
              onError={image_error_replace}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-gray-100"
            />
            {item.student_uuid ? (
              <Link
                to={`/studentprofile?id=${encodeURIComponent(item.student_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                {item.student_name || item.student_uuid}
              </Link>
            ) : (
              <span className="font-medium text-gray-800">{item.student_name || '—'}</span>
            )}
          </div>
        </td>
        <td className="text-left px-5 py-3">{this.member_status_badge(item.overall_status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-gray-700">
          {item.checkin_date || '—'}
        </td>
        <td className="text-left px-5 py-3">{this.check_badge(item)}</td>
        <td className="text-left px-5 py-3">{this.status_badge(item.status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-gray-700">
          {item.agent_name || '—'}
        </td>
        <td className="text-left px-5 py-3">
          <button
            type="button"
            onClick={() => this.open_form(item)}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {submitted ? 'View' : 'Open Form'}
          </button>
        </td>
      </tr>
    );
  };

  render_body = (rows) => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="px-5 py-8 text-center text-gray-500">
            No checklists match these filters.
          </td>
        </tr>
      );
    }
    return rows.map(this.render_row);
  };

  render() {
    const {
      loading,
      form_open,
      selected_item,
      date_from,
      date_to,
      agent_filter,
      status_filter,
      check_filter
    } = this.state;
    const rows = this.get_filtered();

    return (
      <div>
        <CommonHeader title="Early Retention Checklist" loading={loading} />

        <section
          className="bg-white rounded-lg shadow-sm border border-gray-200"
          style={{ margin: '20px 5%' }}
        >
          <div className="px-5 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold">Checklist</h2>
              <span className="text-xs text-gray-500">
                {rows.length} {rows.length === 1 ? 'item' : 'items'}
              </span>
              <button
                type="button"
                onClick={this.load_data}
                disabled={loading}
                aria-label="Refresh checklists"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 28,
                  width: 28,
                  borderRadius: 9999,
                  border: '1px solid #e5e7eb',
                  background: 'var(--surface-card)',
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  padding: 0
                }}
              >
                <FaSyncAlt size={13} color="#f59e0b" className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <DateRangeFilter
                from={date_from}
                to={date_to}
                onChange={this.set_date_range}
                onClear={this.clear_date_range}
              />
              <MultiSelectDropdown
                label="Agent"
                options={this.agent_options()}
                selected={agent_filter}
                onChange={this.set_agent_filter}
                placeholder="All agents"
              />
              {this.render_segmented(STATUS_FILTERS, status_filter, this.set_status_filter)}
              {this.render_segmented(CHECK_FILTERS, check_filter, this.set_check_filter)}
            </div>
          </div>

          {loading ? (
            <CustomLoading />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {this.render_sort_th('Member', 'member')}
                    <th className="text-left px-5 py-2 font-medium">Member Health</th>
                    {this.render_sort_th('Checkin date', 'checkin_date')}
                    {this.render_sort_th('Check', 'check')}
                    <th className="text-left px-5 py-2 font-medium">Checklist Status</th>
                    <th className="text-left px-5 py-2 font-medium">Agent</th>
                    <th className="text-left px-5 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>{this.render_body(rows)}</tbody>
              </table>
            </div>
          )}
        </section>

        <CustomModal
          show={form_open}
          close={this.close_form}
          isFullWidth
          inner_style={{ width: '1100px', maxWidth: '95vw' }}
        >
          {form_open ? (
            <ChecklistForm
              uuid={selected_item.uuid}
              read_only={selected_item.status === 'submitted'}
              close={this.close_form}
              on_submitted={this.on_submitted}
              on_saved_draft={this.on_saved_draft}
            />
          ) : null}
        </CustomModal>
      </div>
    );
  }
}

EarlyRetentionChecklist.propTypes = {
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(EarlyRetentionChecklist);
